import { useState } from "react";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "../../../shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalFooter } from "../../../shared/wrappers/FixaModal/FixaModalFooter";
import {
  FixaSelect,
  FixaOption,
} from "../../../shared/wrappers/FixaSelect/FixaSelect";
import FixaButton from "../../../shared/wrappers/FixaButton/FixaButton";
import {
  useGetAllPermissions,
  useCreateCountryPermission,
} from "../../repositories/permissions.repository";
import { FixaModalBody } from "../../../shared/wrappers/FixaModal/FixaModalBody";
import { BasePermission } from "../../types/BasePermission";
import { useCountries } from "@coworker/app/src/core/hooks/useCountries";
import FixaCheckbox from "../../../shared/wrappers/FixaCheckbox/FixaCheckbox";
import FixaInlineMessage from "../../../shared/wrappers/FixaInlineMessage/FixaInlineMessage";

type Props = {
  isVisible: boolean;
  setIsOpen: (open: boolean) => void;
};

const EnableCountryPermissionModal = ({ isVisible, setIsOpen }: Props) => {
  const [permissionId, setPermissionId] = useState("");
  const [countryId, setCountryId] = useState("");
  const [value, setValue] = useState(true);

  const { data: permissions = [], isLoading: isLoadingPermissions } =
    useGetAllPermissions();
  const { data: countries = [], isLoading: isLoadingCountries } =
    useCountries();
  const createCountryPermissionMutation = useCreateCountryPermission();

  const sortedCountries = [...countries].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const handleAddClick = async () => {
    try {
      await createCountryPermissionMutation.mutateAsync({
        permission_id: permissionId,
        country_id: countryId,
        value,
      });
      setPermissionId("");
      setCountryId("");
      setValue(true);
      setIsOpen(false);
    } catch {
      // createCountryPermissionMutation.isError will show error message
    }
  };

  const handleClose = () => {
    setPermissionId("");
    setCountryId("");
    setValue(true);
    setIsOpen(false);
  };

  return (
    <FixaModal visible={isVisible} handleCloseBtn={handleClose}>
      <FixaSheets
        labelledById="feature-flags-enable-country-permission-modal-header"
        alignment="right"
        preserveAlignment={false}
        size="small"
        fullSize
        header={
          <FixaModalHeader
            id="feature-flags-enable-country-permission-modal-header"
            title={"Enable permission for country"}
            floating={false}
          />
        }
        footer={
          <FixaModalFooter>
            <FixaButton
              onClick={handleAddClick}
              disabled={!permissionId || !countryId}
            >
              Add
            </FixaButton>
          </FixaModalFooter>
        }
      >
        <FixaModalBody>
          <FixaSelect
            id={"permissionIdSelect"}
            value={permissionId}
            onChange={(e) => setPermissionId(e.target.value)}
            label={"Permission code"}
            disabled={isLoadingPermissions}
          >
            <FixaOption value="" name="Select a permission" />
            {permissions.map((permission: BasePermission) => (
              <FixaOption
                key={permission.code}
                value={permission.code}
                name={`${permission.code} - ${permission.description}`}
              />
            ))}
          </FixaSelect>
          <FixaSelect
            id={"countrySelect"}
            value={countryId}
            onChange={(e) => setCountryId(e.target.value)}
            label={"Country"}
            disabled={isLoadingCountries}
          >
            <FixaOption value="" name="Select a country" />
            {sortedCountries.map((c) => (
              <FixaOption
                key={c.isoCode}
                value={c.isoCode}
                name={`${c.isoCode} - ${c.name}`}
              />
            ))}
          </FixaSelect>
          <FixaCheckbox
            id="valueCheckbox"
            value="enabled"
            checked={value}
            onChange={() => setValue(!value)}
            label="Enabled"
          />
          {createCountryPermissionMutation.isError && (
            <FixaInlineMessage
              variant="negative"
              title="Something went wrong. Please try again."
            />
          )}
        </FixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
};

export default EnableCountryPermissionModal;
